import AddIcon from "@mui/icons-material/Add";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HelpIcon from "@mui/icons-material/Help";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { produce } from "immer";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useOutletContext } from "react-router";
import AssetClassChip from "../../../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import AssetTypeFromObjectChip from "../../../../../Components/Misc/Chips/AssetChip/asset-type-chip-from-object";
import IngestStatusChip from "../../../../../Components/Misc/Status/IngestRequest/ingest-status";
import { ModalContext } from "../../../../../Components/Modals";
import ModalAsperaCredentials, {
  ModalAsperaCredentialsProps,
} from "../../../../../Components/Modals/ModalAsperaCredentials/modal-aspera-credentials";
import ModalAsperaUpload, {
  ModalAsperaUploadElementProps,
} from "../../../../../Components/Modals/ModalAsperaUpload/modal-aspera-upload";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalEditMetadata, {
  ModalEditMetadataElementProps,
} from "../../../../../Components/Modals/ModalEditMetadata/modal-edit-metadata";
import ModalNewAssignment, {
  ModalNewAssignmentElementProps,
} from "../../../../../Components/Modals/ModalNewAssignment/modal-new-assignment";
import ModalSetFollowers, {
  ModalSetFollowersElementProps,
} from "../../../../../Components/Modals/ModalSetFollowers/modal-set-followers";
import AssetPanel from "../../../../../Components/Panels/AssetPanel/asset-panel";
import NewRequestPanel from "../../../../../Components/Panels/NewRequestPanels/new-request-panel";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableCell3Lines from "../../../../../Components/Table/TableCell3Lines/table-cell-3-lines";
import TableCellActionReportIssue from "../../../../../Components/Table/TableCellActionReportIssue/table-cell-action-report-issue";
import TableCellBool from "../../../../../Components/Table/TableCellBool";
import TableCellMovie from "../../../../../Components/Table/TableCellMovie/table-cell-movie";
import TableCellOwner2Lines from "../../../../../Components/Table/TableCellOwner2Lines/table-cell-owner-2-lines";
import { EventNotifierOperation } from "../../../../../Core/Api/notifier-client";
import { assetService, cacheService, eventsNotifier, ingestService } from "../../../../../Core/Api/services";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import { ProviderNewAssignmentDto } from "../../../../../Core/Api/Types/db-lab-provider-infos";
import { IngestProcessingState } from "../../../../../Core/Api/Types/ingest-processing-state";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import { routePaths } from "../../../../../Router/router";
import { Distinct, GetEnumValueLabelMap, UserHasAcl, UserHasAcls } from "../../../../../Utils/helpers";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";

import { useTranslation } from "react-i18next";
import { TokenOutlined, VerifiedOutlined } from "@mui/icons-material";
import TableUuids from "../../../../../Components/Table/table-uuids";
export interface IngestRequestedRows {
  id: number;
  ingestStatus: IngestProcessingState | null;
  fileSize: number;
  createdByUserName: string;
  assetId: number;
  assetParentTitleId: number | null;
  assetParentTitleName: string;
  assetType: ObjectType;
  assetClass: ObjectClass;
  assetStatus: ObjectStatus;
  assetParentName: string;
  assetName: string;
  assetAnnexFormatIds: number[];
  assetIsHdr: boolean;
  assetProviderId: number;
  createdAt: string;
  providerName: string;
  followersIds: number[];
  lockedOrderItemIds: number[] | null;
  comment: string | null;
  retries: number;
  placeholderName: string | null;
  custom: { woId: string | null; billingStatus: string | null; ingestStatus: string | null } | null;
  asset: DbAssetObjectInfos | null;
}

export interface IngestRequestedProps {
  rows: IngestRequestedRows[];
  filtersData: {
    createdByNames: string[];
    providersNames: string[];
  };
}

function IngestRequested() {
  // Context
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // #region states
  // Top filters
  const [filtersData, setFiltersData] = useState<IngestRequestedProps["filtersData"]>({
    createdByNames: [],
    providersNames: [],
  });
  const [providerFilter, setProviderFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<ObjectType | "">("");
  const [classFilter, setClassFilter] = useState<ObjectClass | "">("");
  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItemIds, setSelectedItems } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<number[]>>;
    selectedItemIds: number[];
  }>();

  // Loading
  const [isLoading, setIsLoading] = useState(true);

  // Table rows
  const [rows, setRows] = useState<IngestRequestedRows[]>([]);

  // Asset panel and request panel
  const [openAssetPanel, setOpenAssetPanel] = useState(false);
  const [openNewRequestPanel, setOpenNewRequestPanel] = useState(false);

  // Data for modal
  const [newAssignmentProviders, setNewAssignmentProviders] = useState<ProviderNewAssignmentDto[]>([]);

  // Acls
  const aclFollowers = UserHasAcl(AclTypes.aclWorkflowAssignFollowers);
  const aclAssign = UserHasAcl(AclTypes.aclIngestAssignToLabProvider);
  const aclDelete = UserHasAcls([AclTypes.aclIngestDeleteRequest, AclTypes.aclElevationWishlistCreate]);
  const aclEditMetadata = UserHasAcl(AclTypes.aclMetadataUpdate);
  const aclAdminOrSuperUser = UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclAdmin]);

  // Cloud
  const isCloud = cacheService.GetPlatformSettings()?.cloud ?? false;
  // #endregion

  // #region Refs and custom hooks
  //   const selectedAssetId = useRef(undefined);
  const [selectedAssetId, setSelectedAssetId] = useState<number>();
  const [selectedRowId, setSelectedRowId] = useState<number>();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const apiGrid = useGridApiRef();
  // #endregion

  // #region functions
  const handleChangeProviderSelect = (event: SelectChangeEvent) => {
    const providerFilterValue = event.target.value as string;
    setProviderFilter(providerFilterValue);
  };

  const handleFilterType = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    if (value === "") setTypeFilter("");
    else setTypeFilter(ObjectType[value]);
  };

  const handleFilterClass = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    if (value === "") setClassFilter("");
    else setClassFilter(ObjectClass[value]);
  };

  // Handlers
  const handleSetFollowers = (assetId: number, actualFollowersIds: number[]) => {
    if (!openModal) return;

    openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
      objectIds: [assetId],
      objectType: "asset",
      currentFollowersIds: actualFollowersIds,
    });
  };

  const handleNewAssignment = (assetId: number, assetProviderId: number) => {
    if (!openModal) return;
    openModal<ModalNewAssignmentElementProps>(ModalNewAssignment, {
      assetId,
      assetProviderId,
      availableProviders: newAssignmentProviders.length > 0 ? newAssignmentProviders : undefined,
    });
  };

  const handleEditMetadataClick = (assetId: number) => {
    if (!openModal) return;

    assetService.GetObjectById(assetId).then((res) => {
      if (res.success && res.graph) {
        openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [res.graph] });
      } else enqueueCustomSnackbar(res.message || t("alert:error.assetNotFound"), "error");
    });
  };

  const handleAsperaUploadClick = (
    assetId: number,
    assetParentName: string,
    assetName: string,
    assetType: ObjectType,
    assetAnnexFormatIds: number[],
    assetIsHdr: boolean,
    assetClassification: ObjectClass,
    assetProviderId: number
  ) => {
    if (!openModal) return;
    openModal<ModalAsperaUploadElementProps>(ModalAsperaUpload, {
      assetId,
      assetParentName,
      assetName,
      assetType,
      assetAnnexFormatIds,
      assetIsHdr,
      assetClassification,
      assetProviderId,
      useToken: isCloud,
    });
  };

  const handleHelpClick = () => {
    if (!openModal) return;
    openModal<ModalAsperaCredentialsProps>(ModalAsperaCredentials, {
      closeModal: () => {},
    });
  };

  const handleDeleteAsset = (assetId: number, comment?: string) => {
    ingestService.DeleteRequest({ id: assetId, comment: comment || null }).then((response) => {
      if (response.objectId === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
        enqueueCustomSnackbar(response.message!, "primary");
      else {
        if (response.success)
          enqueueCustomSnackbar(t("alert:success.deletedAssetSuccessfully", { id: assetId }), "success");
        else enqueueCustomSnackbar(t("alert:error.deletingAsset", { id: assetId, message: response.message }), "error");
      }
    });
  };

  const handleDeleteAssetClick = (assetId: number) => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      title: t("modal:title.confirmAssetDeletion"),
      onConfirm: (comment?: string) => handleDeleteAsset(assetId, comment),
    });
  };

  // Asset panel
  const hideAssetPanel = () => {
    setOpenAssetPanel(false);
  };

  const showAssetPanel = (e: GridRowParams) => {
    setSelectedAssetId(e.row["assetId"]);
    setSelectedRowId(e.row.id);
    if (!openAssetPanel) setOpenAssetPanel(true);
  };

  const hideNewRequestPanel = () => {
    setOpenNewRequestPanel(false);
  };

  const showNewRequestPanel = () => {
    setOpenNewRequestPanel(true);
  };
  // #endregion

  // #region Grid definition
  const ingestProcessingColumns: GridColDef<IngestRequestedRows>[] = useMemo(
    () => [
      {
        headerName: t("menu:item.objectId"),
        field: "assetId",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => (
          <Link
            color="primary"
            underline="hover"
            component={RouterLink}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            to={`${routePaths.asset.path}/${data.value}`}
          >
            {data.value}
          </Link>
        ),
      },
      {
        headerName: t("common:title"),
        field: "assetParentTitleId",
        minWidth: CellSize.large,
        flex: CellSize.large,
        valueGetter: (_, row) => {
          return `${row.assetParentTitleName} #${row.assetParentTitleId}`;
        },
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => (
          <TableCellMovie
            id={data.row.assetParentTitleId ?? data.row.assetId}
            name={data.row.assetParentTitleName}
            isDrive={data.row.assetParentTitleId === null}
          />
        ),
      },
      {
        headerName: t("table:headerName.contentAndAsset"),
        field: "assetName",
        minWidth: CellSize.xlarge,
        flex: CellSize.xlarge,
        valueGetter: (_, row) => `${row.assetParentName} - ${row.assetName} - ${row.placeholderName}`,
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => (
          <TableCell3Lines
            title={data.row.assetParentName}
            desc={data.row.assetName}
            bottom={
              data.row.placeholderName
                ? t("table:renderCell.templateName", { name: data.row.placeholderName })
                : undefined
            }
          />
        ),
      },
      {
        headerName: t("common:upperCaseCreation"),
        field: "createdAt",
        minWidth: CellSize.large,
        flex: CellSize.large,
        valueGetter: (_, row) => {
          if (!row) return;
          return `${row.createdAt.toString()} ${row.createdByUserName}`;
        },
        valueFormatter: (_, row) => {
          if (row) {
            return `${row.createdByUserName || ""} - ${
              moment(row.createdAt).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS) || ""
            }`;
          } else return "";
        },
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => (
          <TableCellOwner2Lines
            userName={data.row.createdByUserName}
            groupName={moment(data.row.createdAt).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
          />
        ),
      },
      {
        headerName: t("common:type"),
        field: "assetType",
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectType),
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) =>
          data.row.asset && <AssetTypeFromObjectChip size="small" obj={data.row.asset} />,
      },
      {
        headerName: t("common:class"),
        field: "assetClass",
        minWidth: CellSize.xsmall,
        flex: CellSize.xsmall,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectClass),
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => (
          <AssetClassChip size="small" classification={data.row.assetClass} />
        ),
      },
      {
        headerName: t("common:status"),
        field: "ingestStatus",
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(IngestProcessingState),
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) =>
          data.row.ingestStatus && (
            <IngestStatusChip small status={data.row.ingestStatus} fileSize={data.row.fileSize} cloud={isCloud} />
          ),
      },
      {
        headerName: t("table:headerName.try"),
        field: "retries",
        minWidth: CellSize.xxsmall,
        flex: CellSize.xxsmall,
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => <Chip size="small" label={data.row.retries} />,
      },
      {
        headerName: t("table:headerName.orderLock"),
        field: "lockedOrderItemIds",
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueGetter: (_, row) => row?.lockedOrderItemIds !== null && row?.lockedOrderItemIds.length > 0,
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) =>
          data.row.lockedOrderItemIds && <TableCellBool value={data.row.lockedOrderItemIds.length > 0} />,
      },
      {
        headerName: t("table:headerName.internalUsage"),
        field: "internalData",
        type: "singleSelect",
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueGetter: (_, row) => {
          if (!row) return;
          const result: string[] = [];
          row.custom?.woId && result.push("Wo Id");
          row.custom?.ingestStatus && result.push("Ingest");
          row.custom?.billingStatus && result.push("Billing");
          return result;
        },
        valueOptions: ["Wo Id", "Ingest", "Billing"],
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => (
          <Stack sx={{ width: "100%" }}>
            {data.row.custom?.woId && (
              <Stack direction="row" spacing={1}>
                <Typography>{t("table:renderCell.woId")}</Typography>
                <Typography color="text.tier">#{data.row.custom.woId ?? "N/A"}</Typography>
              </Stack>
            )}
            {data.row.custom?.ingestStatus && (
              <Stack direction="row" spacing={1}>
                <Typography>{t("common:ingest")}</Typography>
                <Typography color="text.tier">{data.row.custom.ingestStatus ?? "N/A"}</Typography>
              </Stack>
            )}
            {data.row.custom?.billingStatus && (
              <Stack direction="row" spacing={1}>
                <Typography>{t("common:billing")}</Typography>
                <Typography color="text.tier">{data.row.custom.billingStatus ?? "N/A"}</Typography>
              </Stack>
            )}
          </Stack>
        ),
      },
      {
        headerName: t("common:provider"),
        field: "providerName",
        minWidth: 140,
        flex: 140,
        renderCell: (data: GridRenderCellParams<IngestRequestedRows>) => data.row.providerName,
      },
      {
        headerName: t("common:actions"),
        field: "actions",
        type: "actions",
        align: "right",
        minWidth: CellSize.actionsDefault,
        flex: CellSize.actionsDefault,
        getActions: (params: GridRowParams<IngestRequestedRows>) => {
          if (!params || !params.row) return [];
          let items: any[] = [];

          if (params.row.comment !== null)
            items.push(
              <Tooltip title={params.row.comment}>
                <ChatOutlinedIcon color="info" sx={{ mr: 1 }} />
              </Tooltip>
            );

          items.push(
            <GridActionsCellItem
              icon={<FileUploadOutlinedIcon color="primary" />}
              label={t("common:upload")}
              onClick={() =>
                handleAsperaUploadClick(
                  params.row.assetId,
                  params.row.assetParentName,
                  params.row.assetName,
                  params.row.assetType,
                  params.row.assetAnnexFormatIds,
                  params.row.assetIsHdr,
                  params.row.assetClass,
                  params.row.assetProviderId
                )
              }
            />
          );

          if (aclFollowers)
            items.push(
              <GridActionsCellItem
                icon={<GroupsOutlinedIcon />}
                label={t("common:setFollowers")}
                onClick={() => handleSetFollowers(params.row.assetId, params.row.followersIds)}
                showInMenu
              />
            );

          if (aclAssign)
            items.push(
              <GridActionsCellItem
                icon={<PersonAddIcon />}
                label={t("common:newAssignment")}
                onClick={() => handleNewAssignment(params.row.assetId, params.row.assetProviderId)}
                showInMenu
                disabled={!aclAssign}
              />
            );

          if (aclEditMetadata)
            items.push(
              <GridActionsCellItem
                icon={<EditOutlinedIcon />}
                label={t("common:editMetadata")}
                onClick={() => handleEditMetadataClick(params.row.assetId)}
                showInMenu
              />
            );

          if (aclDelete)
            items.push(
              <GridActionsCellItem
                icon={<DeleteOutlinedIcon color="error" />}
                label={t("common:delete")}
                color="error"
                onClick={() => handleDeleteAssetClick(params.row.assetId)}
                showInMenu
              />
            );

          // id for the tutorial
          items.push(<Box id="tutorial-ingest-request-menu"></Box>);

          if (UserHasAcl(AclTypes.aclSupportTicketCreate)) {
            let platformSettings = cacheService.GetPlatformSettings();
            items.push(
              <TableCellActionReportIssue
                text={`
                    ${t("common:platform")}: ${platformSettings?.name}\n
                    ${t("common:assetIdLowerCase")}: ${params.row.assetId}\n
                    ${t("common:assetName")}: ${params.row.assetName}\n
                    ${t("common:ingestStatus")}: ${params.row.ingestStatus}\n
                    ${t("common:providerName")}: ${params.row.providerName}\n
                    ${t("common:placeholderName")}: ${params.row.placeholderName}
                `}
                typology="Ingest"
                objectId={params.row.assetId}
                showInMenu
                {...params}
              />
            );
          }
          return items;
        },
      },
    ],
    [newAssignmentProviders]
  );
  // #endregion

  // SignalR
  useEffect(() => {
    if (newAssignmentProviders && newAssignmentProviders.length > 0) {
      eventsNotifier.Connect().then(function (result) {
        console.debug("Hub events Connected successfully to WebSocket and is listening to IngestRequestMessage!");
        if (eventsNotifier.connection)
          eventsNotifier.connection.on("IngestRequestMessage", (data) => {
            switch (data.op) {
              case EventNotifierOperation.Create:
                let instanceComputedCreated = ingestService.GetRequestedIngestWorkflowsComputed(
                  data.data,
                  newAssignmentProviders
                );
                // add row to table
                setRows(
                  produce((draft) => {
                    draft.push(instanceComputedCreated);
                  })
                );
                // update filtersData
                setFiltersData(
                  produce((draft) => {
                    if (!draft.createdByNames.includes(instanceComputedCreated.createdByUserName))
                      draft.createdByNames.push(instanceComputedCreated.createdByUserName);
                    if (!draft.providersNames.includes(instanceComputedCreated.providerName))
                      draft.providersNames.push(instanceComputedCreated.providerName);
                  })
                );
                break;
              case EventNotifierOperation.Update:
                let instanceComputedUpdated = ingestService.GetRequestedIngestWorkflowsComputed(
                  data.data,
                  newAssignmentProviders
                );
                // update rows
                setRows(
                  produce((draft) => {
                    const index = draft.findIndex((x) => x.id === instanceComputedUpdated.id);
                    if (index !== -1) draft[index] = instanceComputedUpdated;
                  })
                );
                // update filtersData
                setFiltersData(
                  produce((draft) => {
                    if (!draft.createdByNames.includes(instanceComputedUpdated.createdByUserName))
                      draft.createdByNames.push(instanceComputedUpdated.createdByUserName);
                    if (!draft.providersNames.includes(instanceComputedUpdated.providerName))
                      draft.providersNames.push(instanceComputedUpdated.providerName);
                  })
                );
                break;
              case EventNotifierOperation.Delete:
                // delete row from table
                setRows(
                  produce((draft) => {
                    const index = draft.findIndex((x) => x.id === data.data.id);
                    if (index !== -1) draft.splice(index, 1);
                  })
                );
                break;
              // no need to update filtersData
            }
          });
      });

      // Disconnect from HUB
      return function cleanup() {
        eventsNotifier.Disconnect().then(function (result) {});
      };
    }
  }, [newAssignmentProviders]);

  // #region hooks
  useEffect(() => {
    ingestService.GetRequestedIngestWorkflowsWithProviders().then(([request, providers]) => {
      setRows(request.rows);
      setFiltersData(request.filtersData);
      setNewAssignmentProviders(providers);
      setIsLoading(false);
    });
  }, []);
  // #endregion

  const additionalFilters = (
    <>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<ManageAccountsOutlinedIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={providerFilter}
        onChange={handleChangeProviderSelect}
      >
        <MenuItem value="" selected>
          {t("common:provider")}
        </MenuItem>
        {filtersData.providersNames.length > 0 &&
          filtersData.providersNames.map((providerName) => (
            <MenuItem key={providerName} value={providerName}>
              {providerName}
            </MenuItem>
          ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<TokenOutlined />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={typeFilter}
        onChange={handleFilterType}
      >
        <MenuItem value="" selected>
          {t("common:type")}
        </MenuItem>
        {rows
          .map((x) => x.assetType)
          .filter(Distinct)
          .map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<VerifiedOutlined />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={classFilter}
        onChange={handleFilterClass}
      >
        <MenuItem value="" selected>
          {t("common:class")}
        </MenuItem>
        {rows
          .map((x) => x.assetClass)
          .filter(Distinct)
          .map((x) => (
            <MenuItem key={x} value={x}>
              {x}
            </MenuItem>
          ))}
      </Select>
    </>
  );

  return (
    <Stack sx={{ flex: "1 1 0", minHeight: 0 }}>
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.INGEST_REQUESTED_TABLE}
        columns={ingestProcessingColumns}
        rows={rows.filter(
          (x) =>
            (providerFilter === "" || x.providerName === providerFilter) &&
            (classFilter === "" || x.assetClass === classFilter) &&
            (typeFilter === "" || x.assetType === typeFilter)
        )}
        rowHeight={64}
        getRowHeight={() => "auto"}
        apiRef={apiGrid}
        pagination={true}
        initialColumnVisibility={{
          lockedOrderItemIds: false,
          retries: false,
          internalData: false,
        }}
        additionalActions={
          <>
            <Button variant="outlined" color="primary" startIcon={<HelpIcon />} onClick={handleHelpClick}>
              {t("button:helpAndDoc")}
            </Button>
            {UserHasAcl(AclTypes.aclIngestPlaceholderCreate) && (
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={showNewRequestPanel}>
                {t("button:newRequest")}
              </Button>
            )}
          </>
        }
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
        onRowClick={showAssetPanel}
        checkboxSelection
        disableRowSelectionOnClick
        selectedRows={selectedItemIds}
        setSelectedRows={setSelectedItems}
        additionalFilters={additionalFilters}
      />
      <AssetPanel
        open={openAssetPanel}
        onClose={hideAssetPanel}
        closePanel={hideAssetPanel}
        assetId={selectedAssetId}
        reload={selectedRowId}
      />
      <NewRequestPanel type="create" open={openNewRequestPanel} onClose={hideNewRequestPanel} />
    </Stack>
  );
}

export default IngestRequested;
